import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Follitropin alfa — the first medication in the catalogue that *is* the cycle.
 *
 * Everything walked before this happens once, or happens daily to hold a door
 * shut: a trigger is one dose at an hour the clinic set, an antagonist stops a
 * surge. This one grows the follicles. It is the injection a patient gives most
 * often — eight to fourteen evenings running, sometimes twice a day alongside
 * an antagonist — and the one whose dose her clinic changes mid-course on a
 * scan result.
 *
 * ### Why it is `stimulation` and not something that already existed
 *
 * The trigger-shot step of the care hub filters the registry by indication, and
 * a gonadotropin appearing there would be offering her the wrong procedure at
 * the one moment in the cycle with a deadline. It is reached from a medication
 * row instead, like the antagonists — which is also the honest answer to what
 * it is: a daily dose with no hour of its own.
 *
 * ### Two devices, two procedures
 *
 * A pen dials a dose and clicks; a vial is drawn up through one needle and
 * injected through another. They are the same molecule into the same abdomen,
 * and nothing else about them is the same, so they are two protocols the way
 * Ovidrel's pen and syringe are — chosen by `form`, never guessed.
 *
 * The dose is deliberately absent from both. It is titrated per patient and
 * changed mid-cycle, so a number in a step would be wrong for almost everyone
 * reading it; `no step hardcodes a dose` in `protocol-contract.test.ts` is the
 * guard, and this is the medication it was written for.
 *
 * ### One site
 *
 * The content names the lower abdomen and nothing else, so there is no site
 * question here and the camera guide outlines the area it already knows. Where
 * a medication's content offers the thigh as well — buserelin, growth hormone —
 * that protocol will carry `sites`; this one does not, and adding one would be
 * the aid deciding the medicine.
 */

/**
 * One pen, and not the three this used to claim.
 *
 * Ovaleap and Bemfola were here because they are the same molecule. They are
 * not the same device, and that is the distinction this list exists to make:
 * **Bemfola is a single-use pen** — one injection, then the pen is discarded,
 * with a restricted list of deliverable doses per strength and no 12.5 IU
 * setting — and **Ovaleap is a reusable pen that takes a cartridge**, sold
 * without the pen, the only follitropin alfa presentation that works that way.
 * Neither can follow the steps below.
 *
 * This is not a theoretical tidy-up. ANSM's committee on biosimilar
 * substitution wrote it out in these three categories and added that dose
 * errors "sont déjà constatées actuellement", while approving substitution at
 * the pharmacy counter mid-protocol. Walking a Bemfola patient through "keep
 * the pen for 28 days" is one of the errors they mean.
 *
 * Cinnal-f is gone for a harder reason: it is built on a general-purpose
 * injection pen whose window is graduated in device units, not IU, with 1
 * graduation = 6.25 IU. "Dial your prescribed dose" would deliver 6¼ times it.
 *
 * Each of them needs its own protocol. Bemfola now has one. Ovaleap and
 * Cinnal-f reach the factsheet and no walk, which is the honest answer until
 * their own instructions-for-use can be read rather than assumed.
 */
const GONAL_F_BRANDS = [
  {
    name: "Gonal-F",
    markets: ["US", "CA", "GB", "EU", "CH", "AU", "NZ", "JP"],
  },
] as const satisfies readonly ProtocolBrand[];

/**
 * A missed dose, without the hour nobody wrote.
 *
 * This said "less than 2 hours late, inject; more than 2 hours, contact your
 * clinic". A literature pass against the EMA product information, the UK emc
 * leaflets and the FDA labels for Gonal-f, Puregon/Follistim, Rekovelle,
 * Menopur, Pergoveris, Luveris and Elonva found that **no gonadotropin label,
 * anywhere, states any time window at all**. Every one of them says the same
 * thing: do not double, talk to your doctor.
 *
 * Three things were wrong with the old sentence, in order of how much they
 * cost her:
 *
 * 1. **It pointed the wrong way.** A patient three hours late on a Sunday
 *    evening read a threshold she had crossed, could not reach anyone, and had
 *    been told the dose was now forbidden — so she skipped a stimulation day on
 *    the authority of a number no regulator wrote. The pharmacokinetics say the
 *    opposite: follitropin alfa has a 24–59 hour half-life and accumulates
 *    three-fold to a steady state, so a few hours is a rounding error. A
 *    week's worth of it is licensed as a single injection (corifollitropin).
 * 2. **The shape is one no label uses.** Labels say "do not double, call".
 *    None says "before N hours you may, after N hours you may not". That is a
 *    different speech act with a different consequence.
 * 3. **The number is borrowed.** The only real figure in the whole class set is
 *    ganirelix's six hours, which exists because a gap past thirty hours lets
 *    an LH surge through — a mechanism FSH does not have. And even there the
 *    label says *inject, and also call*: lateness has never disqualified a dose
 *    in any of these documents.
 *
 * The third sentence below — inject rather than skip when the clinic cannot be
 * reached — is **practice, not label**. No label says it. It rests on the
 * pharmacokinetics and on the absence of any prohibition, and it is the clause
 * that fixes the Sunday evening. It is flagged for clinical review; without it
 * the first two sentences are still a strict improvement on a fabricated cliff.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "gonal-f.missed-dose",
  severity: "caution",
  // `none`, like its neighbours: the sentence is what tells her what to do, and
  // a caution never pauses the walk, so an escalation declared here would
  // promise a destination the session has no moment to reach.
  escalation: "none",
  copy: {
    en: "Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
    fr: "Dose oubliée ou en retard - Ne doublez jamais la dose pour rattraper. Prévenez votre centre dès que possible et suivez ses consignes. Si vous ne pouvez pas les joindre le jour même, faites l'injection quand vous y pensez plutôt que de sauter la dose, et signalez-le à votre prochain rendez-vous.",
  },
  sourceSegment:
    "⚠ Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
};

/**
 * OHSS, named by its symptoms rather than by its name.
 *
 * This is the drug that causes it. The content's own list — weight, pain,
 * nausea, breathing, urine output — is the picture a patient presents with, and
 * the label pairs it with stopping the injections, which is why the sentence
 * starts there.
 */
const WHEN_TO_CALL: ProtocolWarning = {
  id: "gonal-f.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
    fr: "Quand contacter votre centre - Arrêtez d'injecter et appelez votre centre en cas de prise de poids rapide de plus de 1 kg/jour, douleurs abdominales sévères, nausées ou vomissements, difficultés respiratoires, diminution de la diurèse ou signes d'infection au point d'injection.",
  },
  sourceSegment:
    "⚠ When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
};

/**
 * The half of "when to call" that does not belong to a clinic.
 *
 * The content listed difficulty breathing and allergic reactions in the same
 * sentence as rapid weight gain, with one destination: the clinic. Anaphylaxis
 * is not a phone call to a fertility clinic (Anaïs, 2026-09-22, restated for
 * every guided medication on 2026-09-24), so the content was split before this
 * restated it — the same split the antagonists carry, in the same words, so a
 * patient who cannot breathe reads the same sentence whichever syringe is in
 * her hand.
 */
const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "gonal-f.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires soudaines, un gonflement du visage ou de la gorge, une éruption cutanée ou de l'urticaire nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
};

/**
 * The situation a drawn dose has and a prefilled one mostly does not.
 *
 * Added to the content for this medication because it was missing and the
 * procedure needs it more than the ones that already had it: a vial is drawn
 * through a needle, transferred to another, and has air pushed out of it — three
 * chances to lose some of a dose that was titrated to her. The wording is the
 * antagonists', unchanged, because the same situation gets the same action.
 */
const SPILL_OR_INCOMPLETE_DOSE: ProtocolWarning = {
  id: "gonal-f.spill-or-incomplete-dose",
  severity: "stop",
  escalation: "clinic-soon",
  copy: {
    en: "Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
    fr: "Renversement ou dose incomplète - Si vous renversez du produit ou suspectez une dose incomplète, appelez votre centre dès que possible. Ne tentez pas une seconde injection sans avis médical.",
  },
  sourceSegment:
    "⚠ Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
};

/** Everything that stops the walk, in the order a patient would meet it. */
const AT_THE_NEEDLE: ProtocolWarning[] = [WHEN_TO_CALL, BREATHING_OR_ALLERGIC];

/** And the vial's, which a pen answers differently — see `PEN_INCOMPLETE_DOSE`. */
const AT_THE_NEEDLE_WITH_A_SYRINGE: ProtocolWarning[] = [
  ...AT_THE_NEEDLE,
  SPILL_OR_INCOMPLETE_DOSE,
];

/**
 * What an empty pen does mid-injection, which is not a spill.
 *
 * The generic "call your clinic and do not attempt a second injection" is the
 * right answer for a drawn dose and the wrong one here: a multi-dose pen that
 * runs out tells her exactly how much she did not receive, and the label's
 * instruction is to complete it with a second pen. Telling her to wait for a
 * callback would leave a titrated dose short on purpose.
 */
const PEN_INCOMPLETE_DOSE: ProtocolWarning = {
  id: "gonal-f.pen.incomplete-dose",
  severity: "stop",
  escalation: "clinic-soon",
  copy: {
    en: "Incomplete dose from an empty pen - If the dose window shows a number above 0 after your injection, that is how much you still need. Complete it with a new pen. If you spill medication or are unsure how much you received, call your clinic as soon as you can.",
    fr: "Dose incomplète par stylo vide - Si la fenêtre de dose affiche un chiffre supérieur à 0 après l'injection, c'est ce qu'il vous reste à recevoir. Complétez avec un nouveau stylo. Si vous renversez du produit ou si vous n'êtes pas sûre de la quantité reçue, appelez votre centre dès que possible.",
  },
  sourceSegment:
    "⚠ Incomplete dose from an empty pen - If the dose window shows a number above 0 after your injection, that is how much you still need. Complete it with a new pen. If you spill medication or are unsure how much you received, call your clinic as soon as you can.",
};

/**
 * Seven steps, and two of them are the ones a patient skips.
 *
 * The first version of this walk had five and was a fair reading of the
 * catalogue's own summary. It was not a fair reading of the label, and two
 * clinical reviews said so:
 *
 * **The droplet.** On a new pen the label requires her to look for a droplet at
 * the needle tip and, if there is none, to dial 25 and press the air out. Skip
 * it and the first injection of a new pen is silently short — on day one of
 * stimulation, with nothing on screen or in her body to tell her. Our own
 * knowledge base already called this "one of the two steps a coach most needs
 * and a patient most often skips", and the walk did not have it.
 *
 * **The dose window.** After the injection the window shows `0`, or it shows
 * what she did not receive because the pen ran out. That number is the whole
 * reason the device has a window, and the label's answer to it is a second pen,
 * not a phone call.
 *
 * The other corrections are smaller and the same kind: the needle is the one in
 * her pack rather than a gauge we named (the European label states none at all,
 * and the American one supplies 31 G × 5/16″, not the 29–30 G this said); the
 * pen comes out of the fridge thirty minutes early and never near a microwave;
 * the needle comes off before the pen is put away; and the pen is finished
 * after 28 days whatever is left in it.
 */
const PEN_STEPS: ProtocolStep[] = [
  {
    id: "gonal-f.pen.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash hands and check the pen expiry date. Take the pen out of the refrigerator and let it sit at room temperature for at least 30 minutes before you inject - do not use a microwave or any other heating element to warm it. Attach a new pen needle from your pack and remove both caps.",
      fr: "Préparer - Lavez-vous les mains et vérifiez la date de péremption du stylo. Sortez le stylo du réfrigérateur et laissez-le à température ambiante au moins 30 minutes avant d'injecter - n'utilisez ni micro-ondes ni aucun autre moyen de chauffage. Fixez une nouvelle aiguille de votre boîte et retirez les deux capuchons.",
    },
    warnings: [MISSED_DOSE],
  },
  {
    // `verify` and a confirmation, because it is a look she has to actually
    // take. The step exists to be the thing between her and the injection.
    id: "gonal-f.pen.inspect",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check for a droplet - On a new pen only, hold it with the needle pointing up and look closely at the needle tip for a tiny droplet of liquid. If you see one, go on to the next step. If you do not, turn the dose knob to 25, keep the needle pointing up, tap the reservoir gently and press the knob all the way in. If no droplet appears after repeating this, contact your clinic. On a pen you have already used, this check is not needed.",
      fr: "Vérifier la gouttelette - Sur un stylo neuf seulement, tenez-le aiguille vers le haut et regardez attentivement la pointe : une gouttelette de liquide doit apparaître. Si vous la voyez, passez à l'étape suivante. Sinon, tournez le sélecteur sur 25, gardez l'aiguille vers le haut, tapotez doucement le réservoir et enfoncez le bouton à fond. Si aucune gouttelette n'apparaît après avoir recommencé, contactez votre centre. Sur un stylo déjà utilisé, cette vérification n'est pas nécessaire.",
    },
  },
  {
    id: "gonal-f.pen.ready-the-pen",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Set your dose - Dial your prescribed dose and confirm the correct number appears in the dose window.",
      fr: "Régler la dose - Sélectionnez votre dose prescrite et vérifiez que le bon chiffre s'affiche dans la fenêtre.",
    },
  },
  {
    id: "gonal-f.pen.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Clean the injection site - Swab a spot on your lower abdomen (at least 2 inches from the navel, alternating sides daily) with alcohol and let it air-dry.",
      fr: "Désinfecter le site - Désinfectez une zone du bas du ventre (à au moins 5 cm du nombril, en alternant les côtés chaque jour) à l'alcool et laissez sécher.",
    },
  },
  {
    id: "gonal-f.pen.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Pinch the skin firmly, insert the needle at a 90° angle, and press the dose button slowly and steadily. You will hear a click when the injection starts. Hold the button down and keep the needle in place for 10 seconds to ensure full dose delivery, then withdraw the needle before you release the button.",
      fr: "Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et appuyez sur le bouton d'injection lentement et régulièrement. Vous entendrez un clic lorsque l'injection démarre. Maintenez le bouton enfoncé et l'aiguille en place pendant 10 secondes pour garantir la délivrance complète, puis retirez l'aiguille avant de relâcher le bouton.",
    },
    warnings: AT_THE_NEEDLE,
  },
  {
    // After the needle is out, and its own step rather than a line of
    // aftercare: the number in that window is the only way she can know the
    // dose was short, and a sentence tucked into "apply gentle pressure" is a
    // sentence she reads once.
    id: "gonal-f.pen.inspect-dose-window",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the dose window - Check that the dose window shows 0. If it shows a number higher than 0, the pen is empty and you have not had your full dose: note the number shown and complete it with a new pen.",
      fr: "Vérifier la fenêtre de dose - Vérifiez que la fenêtre de dose affiche 0. Si elle affiche un chiffre supérieur à 0, le stylo est vide et vous n'avez pas reçu la dose complète : notez le chiffre affiché et complétez avec un nouveau stylo.",
    },
    warnings: [PEN_INCOMPLETE_DOSE],
  },
  {
    id: "gonal-f.pen.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Apply gentle pressure with gauze - do not rub. Remove the needle from the pen straight away and dispose of it in a sharps container - never store the pen with a needle attached. Track your injection site to rotate properly. Use the pen for up to 28 days after your first injection, then discard whatever is left.",
      fr: "Après l'injection - Appliquez une légère pression avec une compresse - sans frotter. Retirez l'aiguille du stylo immédiatement et jetez-la dans un collecteur - ne rangez jamais le stylo avec une aiguille dessus. Notez le site utilisé pour bien alterner. Utilisez le stylo jusqu'à 28 jours après la première injection, puis jetez ce qu'il reste.",
    },
  },
];

/**
 * Benzyl alcohol and latex, which only the European pack warns about.
 *
 * The solvent that makes the multidose vial multidose is what a patient can be
 * allergic to, and the needle shield of the syringe it comes in is latex. Both
 * are in the European leaflet and in neither American document — so this
 * warning belongs to this protocol and not to the medication.
 *
 * `caution`, because it is a thing to have said before she starts rather than a
 * reason to stop mid-procedure, and `none` for the same reason the missed dose
 * carries none: the sentence is the action.
 */
const BENZYL_ALCOHOL_AND_LATEX: ProtocolWarning = {
  id: "gonal-f.benzyl-alcohol-and-latex",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Benzyl alcohol and latex - The prepared solution contains benzyl alcohol, which may cause allergic reactions, and the needle shield of the solvent syringe contains latex. Tell your clinic before you start if you are allergic to either.",
    fr: "Alcool benzylique et latex - La solution préparée contient de l'alcool benzylique, qui peut provoquer des réactions allergiques, et le protège-aiguille de la seringue de solvant contient du latex. Signalez à votre centre avant de commencer si vous êtes allergique à l'un des deux.",
  },
  sourceSegment:
    "⚠ Benzyl alcohol and latex - The prepared solution contains benzyl alcohol, which may cause allergic reactions, and the needle shield of the solvent syringe contains latex. Tell your clinic before you start if you are allergic to either.",
};

/**
 * The European multidose vial, reconstituted at her kitchen table.
 *
 * Eight steps, because reconstitution is four things and not one: inject the
 * solvent, throw the solvent syringe away, swirl and look, then draw. The step
 * that throws the syringe away is not housekeeping — it is the syringe she must
 * not inject with, and the leaflet says so on its own line.
 *
 * The air goes in first, and that is the European technique specifically: pull
 * the plunger to the dose in IU, push that air into the inverted vial, then draw
 * to the same mark. The American leaflet has her overshoot the mark and push
 * back down until a droplet appears. A patient doing one while reading the other
 * fights her syringe while measuring a dose titrated for her — which is why this
 * protocol carries `markets`.
 */
const VIAL_STEPS: ProtocolStep[] = [
  {
    id: "gonal-f.vial.prepare",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Prepare - Wash hands thoroughly. Remove the protective caps from the powder vial and from the pre-filled syringe of solvent.",
      fr: "Préparer - Lavez-vous soigneusement les mains. Retirez les capuchons protecteurs du flacon de poudre et de la seringue préremplie de solvant.",
    },
    warnings: [MISSED_DOSE, BENZYL_ALCOHOL_AND_LATEX],
  },
  {
    id: "gonal-f.vial.reconstitute",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Reconstitute - Insert the needle into the powder vial and slowly inject all the solvent into the vial containing the powder. Remove the syringe from the vial and throw it away. This vial contains several doses - you will keep it several days and draw only the prescribed dose each day. Write on the vial the day you prepared the solution.",
      fr: "Reconstituer - Insérez l'aiguille dans le flacon de poudre et injectez lentement tout le solvant dans le flacon contenant la poudre. Retirez la seringue du flacon et jetez-la. Ce flacon contient plusieurs doses - vous le garderez plusieurs jours et ne prélèverez que la dose prescrite chaque jour. Inscrivez sur le flacon le jour où vous avez préparé la solution.",
    },
  },
  {
    // A `verify` and a confirmation, because this is the one look that decides
    // whether the vial is usable at all, and it is the step a patient in a
    // hurry on her eighth evening will skip.
    id: "gonal-f.vial.inspect",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the solution - Swirl the vial gently - do not shake. Check that the solution is clear and does not contain any particles.",
      fr: "Vérifier la solution - Agitez doucement le flacon par rotation - ne le secouez pas. Vérifiez que la solution est limpide et ne contient pas de particules.",
    },
  },
  {
    id: "gonal-f.vial.draw-dose",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Draw the dose - Take an administration syringe and fill it with air by pulling the plunger to the correct dose in International Units (IU FSH). Insert the needle into the vial, turn the vial upside down and inject the air into the vial. Draw the prescribed dose by pulling the plunger until it reaches the correct dose in International Units (IU FSH).",
      fr: "Prélever la dose - Prenez une seringue d'injection et remplissez-la d'air en tirant le piston jusqu'à la dose correcte en Unités Internationales (UI FSH). Insérez l'aiguille dans le flacon, retournez le flacon et injectez l'air dans le flacon. Prélevez la dose prescrite en tirant le piston jusqu'à la dose correcte en Unités Internationales (UI FSH).",
    },
  },
  {
    id: "gonal-f.vial.remove-air-bubbles",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Remove air bubbles - If you see air bubbles in the syringe, hold the syringe with the needle pointing upwards and gently flick the syringe until all the air collects at the top. Push the plunger until the air bubbles are gone.",
      fr: "Chasser les bulles d'air - Si vous voyez des bulles d'air dans la seringue, tenez la seringue aiguille vers le haut et tapotez doucement jusqu'à ce que tout l'air se rassemble en haut. Poussez le piston jusqu'à ce que les bulles aient disparu.",
    },
  },
  {
    id: "gonal-f.vial.clean-site",
    kind: "prepare",
    requiresConfirmation: false,
    copy: {
      en: "Clean the injection site - Swab the lower abdomen with an alcohol swab and let it dry. Select a different injection site each day, and do not use the same site on consecutive days.",
      fr: "Désinfecter le site - Désinfectez le bas du ventre à l'alcool et laissez sécher. Choisissez un site d'injection différent chaque jour, et n'utilisez pas le même site deux jours de suite.",
    },
  },
  {
    id: "gonal-f.vial.inject",
    kind: "irreversible",
    requiresConfirmation: true,
    copy: {
      en: "Inject - Firmly pinch the skin together and insert the needle at a 45° to 90° angle using a dart-like motion. Inject under the skin by pushing the plunger gently. Do not inject directly into a vein. Take as much time as you need to inject all the solution. Immediately withdraw the needle and clean the skin with an alcohol swab using a circular motion.",
      fr: "Injecter - Pincez fermement la peau et insérez l'aiguille selon un angle de 45° à 90° d'un geste sec. Injectez sous la peau en poussant doucement le piston. N'injectez pas directement dans une veine. Prenez le temps qu'il vous faut pour injecter toute la solution. Retirez immédiatement l'aiguille et nettoyez la peau avec une compresse alcoolisée, d'un mouvement circulaire.",
    },
    warnings: AT_THE_NEEDLE_WITH_A_SYRINGE,
  },
  {
    id: "gonal-f.vial.aftercare",
    kind: "aftercare",
    requiresConfirmation: false,
    copy: {
      en: "After injection - Immediately discard the used syringe in a sharps container. Store the vial with the prepared solution below 25°C, in its original container, away from light - do not freeze. Do not use any solution left in the vial after 28 days.",
      fr: "Après l'injection - Jetez immédiatement la seringue usagée dans un collecteur d'aiguilles. Conservez le flacon contenant la solution préparée à une température ne dépassant pas 25 °C, dans son emballage d'origine, à l'abri de la lumière - ne pas congeler. N'utilisez pas la solution restant dans le flacon au-delà de 28 jours.",
    },
  },
];

const SHARED = {
  medicationSlug: "gonal-f",
  brands: GONAL_F_BRANDS,
  // No molecule name. "Follitropin alfa" is dispensed as three different
  // devices, and a prescription carrying only the molecule cannot say which —
  // matching it here would start the multi-dose pen walk for a patient holding
  // a single-use one. The trade name is the only thing that identifies the
  // device, which is why `brand.ts` exists.
  aliases: ["Gonal F", "GonalF"],
  indication: "stimulation",
  // In IU, as the approved content doses it ("75-450 IU daily"), and titrated:
  // her clinic sets the number and changes it mid-course, so there are no
  // strengths to check it against. Only the unit can be wrong on a record.
  dose: { unit: "IU" },
  route: "subcutaneous",
  version: "2026-09-25.2",
  derivedFrom: "content/data/medications/gonadotropins.json#follitropin-alfa",
} as const satisfies Partial<InjectionProtocol>;

export const GONAL_F_PEN_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  steps: PEN_STEPS,
};

export const GONAL_F_VIAL_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  form: "vial",
  // Powder and solvent, not "vial and syringe": every follitropin alfa vial is
  // a lyophilised powder she reconstitutes. A patient who picked "flacon +
  // seringue" is describing something this medication is not sold as.
  prescriptionForms: ["lyophilized_powder"],
  // Europe only, and deliberately. The American multidose vial is drawn by a
  // different technique and comes out of a fridge; the American single-dose
  // vial stopped being marketed in February 2025. A walk for the US pack is its
  // own protocol, not a footnote on this one — until it exists, a patient in
  // the US is offered the pen and nothing else, which is honest.
  // The EEA, the UK and Switzerland — every country the European leaflet is the
  // one in her box. Written out rather than abbreviated: `"EU"` is a market
  // code a brand list uses for "the name on the box across the bloc", and
  // `protocolsForMarket` compares against a country, so an `"EU"` entry here
  // would match nobody. The first version of this list named a dozen countries
  // and silently withheld the vial walk from Greece, Czechia, Hungary, Romania
  // and the rest.
  markets: [
    "AT",
    "BE",
    "BG",
    "CH",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GB",
    "GR",
    "HR",
    "HU",
    "IE",
    "IS",
    "IT",
    "LI",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "NO",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
  ],
  steps: VIAL_STEPS,
};

export const GONAL_F_PROTOCOLS: readonly InjectionProtocol[] = [
  GONAL_F_PEN_PROTOCOL,
  GONAL_F_VIAL_PROTOCOL,
];
